import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, TrendingUp, PieChart, Table, Sparkles } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { LessonWrapper, LessonPage } from '../../ui/LessonWrapper';

// ============ LESSON CONTENT ============
const LESSONS: LessonPage[] = [
    {
        id: 1,
        title: "Visualizing Data",
        icon: "📊",
        content: (
            <div className="space-y-6">
                <div className="bg-blue-50 p-6 rounded-2xl border-2 border-blue-200">
                    <h3 className="font-bold text-xl text-blue-800 mb-3">Why Charts?</h3>
                    <p className="text-slate-700 mb-4">
                        Raw data (tables) is hard to read. <strong>Charts</strong> make patterns obvious instantly.
                    </p>
                    <p className="text-slate-700">
                        A Dashboard is a collection of charts that tells a story about what's happening.
                    </p>
                </div>
            </div>
        )
    },
    {
        id: 2,
        title: "Chart Types",
        icon: "📉",
        content: (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-xl border-2 border-slate-200 shadow-sm">
                    <div className="text-amber-500 mb-2"><BarChart3 size={32} /></div>
                    <h4 className="font-bold text-slate-800">Bar Chart</h4>
                    <p className="text-xs text-slate-500 mt-1">Comparing amounts (e.g. Sales per Month).</p>
                </div>
                <div className="bg-white p-4 rounded-xl border-2 border-slate-200 shadow-sm">
                    <div className="text-green-500 mb-2"><TrendingUp size={32} /></div>
                    <h4 className="font-bold text-slate-800">Line Chart</h4>
                    <p className="text-xs text-slate-500 mt-1">Trends over time (e.g. Stock Price).</p>
                </div>
                <div className="bg-white p-4 rounded-xl border-2 border-slate-200 shadow-sm">
                    <div className="text-purple-500 mb-2"><PieChart size={32} /></div>
                    <h4 className="font-bold text-slate-800">Pie Chart</h4>
                    <p className="text-xs text-slate-500 mt-1">Parts of a whole (e.g. Budget Split).</p>
                </div>
            </div>
        )
    },
    {
        id: 3,
        title: "Mission Brief",
        icon: "💼",
        content: (
            <div className="space-y-6">
                <div className="bg-slate-100 p-6 rounded-2xl border-2 border-slate-300">
                    <h3 className="font-bold text-xl text-slate-800 mb-3">The Boardroom</h3>
                    <p className="text-slate-700 mb-4">
                        The CEO wants to see the Banana Sales data.
                    </p>
                    <p className="font-bold text-slate-900">
                        Mission: Build the dashboard by trying out all 3 chart types (Bar, Line, Pie).
                    </p>
                </div>
            </div>
        )
    }
];

// ============ GAME LOGIC ============
const SAMPLE_DATA = [
    { month: 'Jan', gorillas: 120, bananas: 340 },
    { month: 'Feb', gorillas: 150, bananas: 420 },
    { month: 'Mar', gorillas: 180, bananas: 380 },
    { month: 'Apr', gorillas: 220, bananas: 510 },
    { month: 'May', gorillas: 190, bananas: 480 },
    { month: 'Jun', gorillas: 250, bananas: 620 },
];

const PIE_DATA = [
    { name: 'Foundation', value: 35, color: '#22c55e' },
    { name: 'Production', value: 25, color: '#3b82f6' },
    { name: 'AI Operator', value: 20, color: '#a855f7' },
    { name: 'Security', value: 20, color: '#ef4444' },
];

type ChartType = 'bar' | 'line' | 'pie';

const DashboardGame = ({ onComplete }: { onComplete: () => void }) => {
    const [chartType, setChartType] = useState<ChartType>('bar');
    const [selectedMetric, setSelectedMetric] = useState<'gorillas' | 'bananas'>('gorillas');
    const [testedTypes, setTestedTypes] = useState<Set<ChartType>>(new Set(['bar']));
    const [finished, setFinished] = useState(false);

    const maxValue = Math.max(...SAMPLE_DATA.map(d => d[selectedMetric]));

    const handleTypeChange = (type: ChartType) => {
        setChartType(type);
        const newTested = new Set(testedTypes).add(type);
        setTestedTypes(newTested);

        if (newTested.size >= 3 && !finished) {
            setFinished(true);
            onComplete();
        }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Controls */}
            <div className="lg:col-span-3 bg-white rounded-2xl p-6 shadow-lg border-2 border-slate-100 flex flex-wrap items-center justify-between gap-4">
                <div className="flex gap-2">
                    <span className="text-sm font-bold text-slate-500 self-center mr-2">Chart Type:</span>
                    {(['bar', 'line', 'pie'] as ChartType[]).map(type => (
                        <button
                            key={type}
                            onClick={() => handleTypeChange(type)}
                            className={`px-4 py-2 rounded-xl text-sm font-bold border-2 capitalize transition-all flex items-center gap-2 ${chartType === type
                                ? 'bg-amber-500 text-white border-amber-500'
                                : 'bg-white border-slate-200 text-slate-600 hover:border-amber-300'
                                } ${testedTypes.has(type) ? 'ring-2 ring-green-400 ring-offset-1' : ''}`}
                        >
                            {type === 'bar' && <BarChart3 size={16} />}
                            {type === 'line' && <TrendingUp size={16} />}
                            {type === 'pie' && <PieChart size={16} />}
                            {type}
                        </button>
                    ))}
                </div>

                {chartType !== 'pie' && (
                    <div className="flex gap-2">
                        <span className="text-sm font-bold text-slate-500 self-center mr-2">Metric:</span>
                        <button
                            onClick={() => setSelectedMetric('gorillas')}
                            className={`px-4 py-2 rounded-xl text-sm font-bold border-2 transition-all ${selectedMetric === 'gorillas'
                                ? 'bg-green-500 text-white border-green-500'
                                : 'bg-white border-slate-200 text-slate-600'
                                }`}
                        >
                            🦍 Gorillas
                        </button>
                        <button
                            onClick={() => setSelectedMetric('bananas')}
                            className={`px-4 py-2 rounded-xl text-sm font-bold border-2 transition-all ${selectedMetric === 'bananas'
                                ? 'bg-yellow-500 text-white border-yellow-500'
                                : 'bg-white border-slate-200 text-slate-600'
                                }`}
                        >
                            🍌 Bananas
                        </button>
                    </div>
                )}
            </div>

            {/* Chart Area */}
            <div className="lg:col-span-2 bg-white rounded-3xl p-8 shadow-lg border-2 border-slate-100 min-h-[400px]">
                <h2 className="text-lg font-bold text-slate-700 mb-6 flex items-center gap-2">
                    <Sparkles className="text-amber-500" />
                    {chartType === 'pie' ? 'Course Completion Distribution' : `${selectedMetric === 'gorillas' ? '🦍 Gorilla' : '🍌 Banana'} Population by Month`}
                </h2>

                {chartType === 'bar' && (
                    <div className="h-64 flex items-end gap-4">
                        {SAMPLE_DATA.map((d, i) => (
                            <motion.div
                                key={d.month}
                                className="flex-1 flex flex-col items-center"
                                initial={{ height: 0 }}
                                animate={{ height: 'auto' }}
                            >
                                <motion.div
                                    initial={{ height: 0 }}
                                    animate={{ height: `${(d[selectedMetric] / maxValue) * 200}px` }}
                                    transition={{ delay: i * 0.1, type: 'spring' }}
                                    className={`w-full rounded-t-xl ${selectedMetric === 'gorillas' ? 'bg-green-500' : 'bg-yellow-500'}`}
                                />
                                <div className="text-xs font-bold text-slate-500 mt-2">{d.month}</div>
                                <div className="text-xs text-slate-400">{d[selectedMetric]}</div>
                            </motion.div>
                        ))}
                    </div>
                )}

                {chartType === 'line' && (
                    <div className="h-64 relative">
                        <svg viewBox="0 0 500 200" className="w-full h-full">
                            {[0, 50, 100, 150, 200].map(y => (
                                <line key={y} x1="40" y1={y} x2="480" y2={y} stroke="#e2e8f0" strokeWidth="1" />
                            ))}
                            <motion.polyline
                                fill="none"
                                stroke={selectedMetric === 'gorillas' ? '#22c55e' : '#eab308'}
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                initial={{ pathLength: 0 }}
                                animate={{ pathLength: 1 }}
                                transition={{ duration: 1 }}
                                points={SAMPLE_DATA.map((d, i) =>
                                    `${40 + i * 80},${200 - (d[selectedMetric] / maxValue) * 180}`
                                ).join(' ')}
                            />
                            {SAMPLE_DATA.map((d, i) => (
                                <motion.circle
                                    key={d.month}
                                    cx={40 + i * 80}
                                    cy={200 - (d[selectedMetric] / maxValue) * 180}
                                    r="6"
                                    fill={selectedMetric === 'gorillas' ? '#22c55e' : '#eab308'}
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ delay: i * 0.1 }}
                                />
                            ))}
                            {SAMPLE_DATA.map((d, i) => (
                                <text key={d.month} x={40 + i * 80} y="220" textAnchor="middle" className="text-xs fill-slate-500">
                                    {d.month}
                                </text>
                            ))}
                        </svg>
                    </div>
                )}

                {chartType === 'pie' && (
                    <div className="h-64 flex items-center justify-center">
                        <svg viewBox="0 0 200 200" className="w-64 h-64">
                            {PIE_DATA.reduce((acc, d, i) => {
                                const startAngle = acc.currentAngle;
                                const endAngle = startAngle + (d.value / 100) * 360;
                                const x1 = 100 + 80 * Math.cos((startAngle - 90) * Math.PI / 180);
                                const y1 = 100 + 80 * Math.sin((startAngle - 90) * Math.PI / 180);
                                const x2 = 100 + 80 * Math.cos((endAngle - 90) * Math.PI / 180);
                                const y2 = 100 + 80 * Math.sin((endAngle - 90) * Math.PI / 180);
                                const largeArc = d.value > 50 ? 1 : 0;

                                acc.paths.push(
                                    <motion.path
                                        key={d.name}
                                        d={`M100,100 L${x1},${y1} A80,80 0 ${largeArc},1 ${x2},${y2} Z`}
                                        fill={d.color}
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        transition={{ delay: i * 0.1, type: 'spring' }}
                                    />
                                );
                                acc.currentAngle = endAngle;
                                return acc;
                            }, { paths: [] as React.ReactNode[], currentAngle: 0 }).paths}
                        </svg>
                        <div className="ml-8 space-y-2">
                            {PIE_DATA.map(d => (
                                <div key={d.name} className="flex items-center gap-2 text-sm">
                                    <div className="w-4 h-4 rounded" style={{ backgroundColor: d.color }} />
                                    <span className="text-slate-600">{d.name}</span>
                                    <span className="font-bold text-slate-800">{d.value}%</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Data Table */}
            <div className="bg-white rounded-3xl p-6 shadow-lg border-2 border-slate-100">
                <h2 className="text-lg font-bold text-slate-700 mb-4 flex items-center gap-2">
                    <Table size={20} className="text-slate-400" /> Raw Data
                </h2>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b-2 border-slate-100">
                                <th className="text-left p-2 text-slate-500">Month</th>
                                <th className="text-right p-2 text-slate-500">🦍</th>
                                <th className="text-right p-2 text-slate-500">🍌</th>
                            </tr>
                        </thead>
                        <tbody>
                            {SAMPLE_DATA.map((d, i) => (
                                <motion.tr
                                    key={d.month}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: i * 0.05 }}
                                    className="border-b border-slate-50"
                                >
                                    <td className="p-2 font-bold text-slate-700">{d.month}</td>
                                    <td className="p-2 text-right text-green-600">{d.gorillas}</td>
                                    <td className="p-2 text-right text-yellow-600">{d.bananas}</td>
                                </motion.tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

// ============ MAIN COMPONENT ============
export const Dashboarding = () => {
    const { completeModule } = useAcademyStore();
    const [completed, setCompleted] = useState(false);

    const handleComplete = () => {
        setCompleted(true);
        completeModule('dashboarding', 100, 'badge_data-detective');
    };

    return (
        <LessonWrapper
            moduleName="Dashboarding"
            moduleEmoji="📉"
            moduleColor="blue"
            lessons={LESSONS}
            completed={completed}
            activityLabel="Boardroom"
        >
            <DashboardGame onComplete={handleComplete} />
        </LessonWrapper>
    );
};

export default Dashboarding;
